import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { decode } from 'jsonwebtoken';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import { Helmet } from 'react-helmet-async';
import { PAGES } from '../../constants/pages';
import SelectList from '../../components/SelectList/SelectList';
import BaseLayout from '../../components/BaseLayout';
import { Heading, HeadingParagraph } from '../../components/Heading';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import useQuery from '../../util/hooks/useQuery';
import {
  generateToken,
  getBasicAccount,
  setLogonUid,
} from '../../store/actions/user/userActions';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { getApplication } from '../../store/actions/application/applicationActions';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import {
  BASIC_ACCOUNT_UPGRADE_TYPES,
  CREATE_NEW_APPLICATION,
} from '../../util/constants';
import { JWT_TOKEN } from '../../constants/localStorage';
import ExistingApplicationModal from '../../components/Modals/ExistingApplicationModal/ExistingApplicationModal';
import {
  ACCOUNT_TYPE_GTM,
  IS_UPGRADE_FLOW,
  SIGNIFYD_SESSION_ID,
} from '../../constants/sessionStorage';

import { selectSettings } from '../../store/selectors/settingsSelectors';
import loadSignifydDeviceFingerprintScript from '../../signifydDeviceFingerprint';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';

const ChooseApplication = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const query = useQuery();
  const { token } = query;
  const JwtToken = retrieveFromSessionStorage(JWT_TOKEN);
  const isUpgradeFlowFromStorage = retrieveFromSessionStorage(IS_UPGRADE_FLOW);
  const isCreateNewApplicationFlow = retrieveFromSessionStorage(
    CREATE_NEW_APPLICATION,
  );
  const Application = useSelector(selectApplication);
  const { AccountUpgradeType } = Application;
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;
  const [isUpgraded, setIsUpgraded] = useState(false);
  const [isExistingModalOpen, setIsExistingModalOpen] = useState(false);
  const { BrokerName } = useSelector(selectSettings);
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const { gtmStepEvent } = useGtmStepHook();

  // const onSalesAssistedAuthenticateSucces = () => {
  //   history.push(PAGES.CUSTOMER_EMAIL);
  // };

  useEffect(() => {
    if (JwtToken) {
      gtmStepHelper({
        gtmStepEvent,
        eventName: ACCOUNT_STEP_VIEW,
        stepLabel: 'Choose application',
        customParams: {
          timestamp: new Date().toISOString(),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const {
      ApplicationStep,
      ApplicationType,
      AccountUpgradeType,
    } = Application;
    if (isEmpty(Application)) {
      return;
    }
    if (
      !isExistingModalOpen &&
      !isCreateNewApplicationFlow &&
      (ApplicationStep ||
        (ApplicationType &&
          (!AccountUpgradeType ||
            AccountUpgradeType !== BASIC_ACCOUNT_UPGRADE_TYPES.UPGRADED)))
    ) {
      setIsExistingModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application]);

  const redirectToCurrentStep = (ApplicationUid) => {
    const onGetApplicationSuccess = (application) => {
      if (application?.ApplicationStep) {
        if (isUpgrade || isUpgradeFlowFromStorage) {
          setIsExistingModalOpen(true);
          return;
        }
        history.push(PAGES.LOADING);
        document.body.style.overflow = 'unset';
      }
    };
    if (
      ApplicationUid?.length &&
      ApplicationUid !== '00000000-0000-0000-0000-000000000000'
    ) {
      dispatch(
        getApplication({
          data: { ApplicationUid },
          onSuccess: onGetApplicationSuccess,
        }),
      );
    }
  };

  const onSuccess = (JwtToken) => {
    const { PortalLogonUid: logonUid, ApplicationUid } = decode(JwtToken);
    dispatch(setLogonUid({ logonUid }));
    if (ApplicationUid?.length) {
      redirectToCurrentStep(ApplicationUid);
    }
  };

  useEffect(() => {
    if (token) {
      setIsUpgraded(true);
      dispatch(
        generateToken({
          token: token ? `"${token}"` : `"${JwtToken}"`,
          onSuccess,
        }),
      );
    }
    if (JwtToken) {
      const { PortalLogonUid } = decode(JwtToken);
      if (PortalLogonUid) {
        const { ApplicationUid } = decode(JwtToken);
        redirectToCurrentStep(ApplicationUid);
      }
    }
  }, []); //eslint-disable-line

  // useEffect(() => {
  //   if (JwtToken) {
  //     const { ApplicationUid } = decode(JwtToken);
  //     redirectToCurrentStep(ApplicationUid);
  //   }
  // }, [ JwtToken]); //eslint-disable-line

  useEffect(() => {
    if (isUpgrade && Application?.ApplicationUid && Application?.AccountUid) {
      dispatch(
        getBasicAccount({
          applicationUid: Application?.ApplicationUid,
          accountUid: Application?.AccountUid,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, Application]);

  const standardAccountOptions = [
    {
      title: t('selectPath.options.standard.title'),
      subtitle: t('selectPath.options.standard.subtitle'),
      onClick: () => {
        history.push(PAGES.CHOOSE_APPLICATION.STANDARD.MAIN);
        storeInSessionStorage(ACCOUNT_TYPE_GTM, 'Standard');
        gtmStepHelper({
          gtmStepEvent,
          eventName: ACCOUNT_STEP_COMPLETE,
          stepLabel: 'Choose application',
          customParams: {
            timestamp: new Date().toISOString(),
          },
        });
        gtmStepHelper({
          gtmStepEvent,
          eventName: ACCOUNT_STEP_VIEW,
          stepLabel: 'Choose application Standard',
          accountType: 'Standard',
          customParams: {
            timestamp: new Date().toISOString(),
          },
        });
      },
      showDivider: false,
    },
  ];

  const taxAdvantagedAccountOptions = [
    {
      title: t('selectPath.options.retirement.title'),
      subtitle: t('selectPath.options.retirement.subtitle'),
      onClick: () => {
        history.push(PAGES.CHOOSE_APPLICATION.RETIREMENT.MAIN);
        storeInSessionStorage(ACCOUNT_TYPE_GTM, 'Retirement');
        gtmStepHelper({
          gtmStepEvent,
          eventName: ACCOUNT_STEP_COMPLETE,
          stepLabel: 'Choose application',
          accountId: Application?.AccountUid,
          customParams: {
            timestamp: new Date().toISOString(),
          },
        });
        gtmStepHelper({
          gtmStepEvent,
          eventName: ACCOUNT_STEP_VIEW,
          stepLabel: 'Choose application Retirement',
          accountId: Application?.AccountUid,
          accountType: 'IRA',
          customParams: {
            timestamp: new Date().toISOString(),
          },
        });
      },
      showDivider: false,
    },
    {
      title: t('selectPath.options.gift.title'),
      subtitle: t('selectPath.options.gift.subtitle'),
      onClick: () => {
        history.push(PAGES.CHOOSE_APPLICATION.UTMA.MAIN);
        storeInSessionStorage(ACCOUNT_TYPE_GTM, 'UTMA');
        gtmStepHelper({
          gtmStepEvent,
          eventName: ACCOUNT_STEP_COMPLETE,
          stepLabel: 'Choose application',
          accountId: Application?.AccountUid,
          customParams: {
            timestamp: new Date().toISOString(),
          },
        });
        gtmStepHelper({
          gtmStepEvent,
          eventName: ACCOUNT_STEP_VIEW,
          stepLabel: 'Choose application UTMA',
          accountId: Application?.AccountUid,
          accountType: 'UTMA',
          customParams: {
            timestamp: new Date().toISOString(),
          },
        });
      },
      showDivider: false,
    },
  ];

  const continueApplication = () => {
    setIsExistingModalOpen(false);
    history.push(PAGES.LOADING);
    document.body.style.overflow = 'unset';

    loadSignifydDeviceFingerprintScript();
    const script = document.getElementById('sig-api');
    storeInSessionStorage(SIGNIFYD_SESSION_ID, script.dataset.orderSessionId);

    // eslint-disable-next-line no-console
    console.log(
      'NEW CREATED SIGNIFYD_SESSION_ID:',
      script.dataset.orderSessionId,
    );
  };

  const startNewApplication = () => {
    setIsExistingModalOpen(false);
    storeInSessionStorage(CREATE_NEW_APPLICATION, true);
  };

  return (
    <BaseLayout
      previousPath={PAGES.BASE}
      isUpgrade={isUpgraded}
      setEventType={setEventType}
      eventType={eventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <Helmet>
        <title>
          {t('selectPath.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Heading mb={28} fontWeight={700}>
        {isUpgrade || isUpgradeFlowFromStorage
          ? t('selectPath.upgradeAccount.title')
          : t('selectPath.openNewAccount.title')}
      </Heading>
      <HeadingParagraph color={themeColors.colorTextSecondary}>
        {t('selectPath.standardAccount')}
      </HeadingParagraph>
      <Paragraph color={themeColors.colorTextSecondary}>
        {isUpgrade || isUpgradeFlowFromStorage
          ? t('selectPath.upgradeAccount.upgradeYourBasicAccount')
          : t('selectPath.openNewAccount.openAVerifiedAccount')}
      </Paragraph>

      <SelectList options={standardAccountOptions} isChooseApp />
      <HeadingParagraph mt={30} color={themeColors.colorTextSecondary}>
        {t('selectPath.taxAdvantagedAccount')}
      </HeadingParagraph>
      <Paragraph>{t('selectPath.openAnAccountForRetirement')}</Paragraph>

      <SelectList options={taxAdvantagedAccountOptions} isChooseApp />
      {(isUpgrade || isUpgradeFlowFromStorage) && (
        <ExistingApplicationModal
          isOpen={isExistingModalOpen}
          continueApplication={continueApplication}
          startNewApplication={startNewApplication}
        />
      )}
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </BaseLayout>
  );
};

export default ChooseApplication;
